import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Chart } from "../components/Chart";
import { Card, DataGaps, Empty, ErrorState, Headline, Loading } from "../components/ui";
import { api } from "../lib/api";
import { lineOption, palette, type SeriesSpec } from "../lib/charts";
import { useDemo } from "../lib/demo";
import { date, dateShort, money, moneyExact } from "../lib/format";
import type { ProductBrief, SalesReport } from "../lib/types";

/**
 * Sprzedaż: ile pieniędzy wpłynęło każdego dnia albo miesiąca.
 *
 * Definicja (udane płatności, doba biznesowa, waluta raportowa) jest
 * w słowniku metryk i w całości po stronie backendu — także suma wszystkich
 * produktów. Ten ekran tylko wybiera zakres i rysuje.
 */

type Granularity = "day" | "month";

const MONTH_LABEL = new Intl.DateTimeFormat("pl-PL", { month: "short", year: "numeric" });

function monthKey(value: Date) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}`;
}

function shiftMonth(key: string, by: number) {
  const [year, month] = key.split("-").map(Number) as [number, number];
  return monthKey(new Date(year, month - 1 + by, 1));
}

/** Skróty zakresu — liczone od bieżącego miesiąca przeglądarki. */
function presets(current: string) {
  return [
    { key: "biezacy", label: "ten miesiąc", from: current, to: current },
    { key: "poprzedni", label: "poprzedni", from: shiftMonth(current, -1), to: shiftMonth(current, -1) },
    { key: "3m", label: "3 mies.", from: shiftMonth(current, -2), to: current },
    { key: "12m", label: "12 mies.", from: shiftMonth(current, -11), to: current },
    { key: "rok", label: "ten rok", from: `${current.slice(0, 4)}-01`, to: current },
  ];
}

/**
 * Kolor idzie za produktem, nie za pozycją na wykresie: miejsce w palecie
 * wynika z kolejności na pełnej liście produktów, więc odznaczenie jednego nie
 * przemalowuje pozostałych. Paleta ma sześć zwalidowanych miejsc; siódmy
 * produkt dostaje neutralną szarość zamiast wymyślonego koloru.
 */
function useColors() {
  const products = useQuery({
    queryKey: ["products", "wszystkie"],
    queryFn: () => api.get<{ products: ProductBrief[] }>("/products"),
  });
  return useMemo(() => {
    const p = palette();
    const map = new Map<string, string>();
    products.data?.products.forEach((product, index) => {
      map.set(product.slug, p.series[index] ?? p.inkMuted);
    });
    return (slug: string) => map.get(slug) ?? p.inkMuted;
  }, [products.data]);
}

export function SalesPage() {
  const { scoped, key } = useDemo();
  const current = monthKey(new Date());
  const [from, setFrom] = useState(current);
  const [to, setTo] = useState(current);
  const [granularity, setGranularity] = useState<Granularity>("day");
  // `null` = wszystkie produkty; zbiór = wybór ręczny.
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [showTotal, setShowTotal] = useState(true);
  const colorOf = useColors();

  const invalid = from > to;
  const productsParam = picked ? `&products=${[...picked].join(",")}` : "";
  const sales = useQuery({
    queryKey: ["sales", from, to, granularity, productsParam, key],
    queryFn: () =>
      api.get<SalesReport>(
        scoped(`/sales?months=${from}:${to}&granularity=${granularity}${productsParam}`),
      ),
    enabled: !invalid && picked?.size !== 0,
    placeholderData: (previous) => previous,
  });

  // Lista przełączników pochodzi z odpowiedzi bez filtra, żeby odznaczony
  // produkt nie znikał z ekranu razem ze swoją linią.
  const all = useQuery({
    queryKey: ["sales", "lista", from, to, key],
    queryFn: () => api.get<SalesReport>(scoped(`/sales?months=${from}:${to}&granularity=month`)),
    enabled: !invalid,
  });

  const report = sales.data;
  const categories = useMemo(
    () =>
      report?.buckets.map((bucket) =>
        report.granularity === "day" ? dateShort(bucket) : MONTH_LABEL.format(new Date(bucket)),
      ) ?? [],
    [report],
  );

  const option = useMemo(() => {
    if (!report) return null;
    const p = palette();
    const monthly = report.granularity === "month";
    const series: SeriesSpec[] = report.series
      .filter((item) => item.values !== null)
      .map((item) => ({
        name: item.product.name,
        values: item.values!,
        color: colorOf(item.product.slug),
        symbols: monthly,
      }));
    if (!series.length) return null;
    if (showTotal && series.length > 1 && report.total.values) {
      series.push({
        name: "Wszystkie produkty",
        values: report.total.values,
        color: p.ink,
        dashed: true,
        symbols: monthly,
      });
    }
    const currency = report.reporting_currency;
    return lineOption(categories, series, (value) => money(value, currency, { compact: true }));
  }, [report, categories, colorOf, showTotal]);

  const toggle = (slug: string) => {
    const everything = all.data?.series.map((item) => item.product.slug) ?? [];
    const next = new Set(picked ?? everything);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    setPicked(next.size === everything.length ? null : next);
  };

  const presetList = presets(current);
  const activePreset = presetList.find((item) => item.from === from && item.to === to)?.key;
  const currency = report?.reporting_currency ?? "PLN";
  const measured = report?.series.filter((item) => item.values !== null) ?? [];
  const gaps = [
    ...(report?.warnings ?? []),
    ...(report?.total.reason && measured.length ? [`Suma: ${report.total.reason}.`] : []),
  ];

  return (
    <>
      <header className="page-head">
        <div>
          <p className="page-head__eyebrow">Portfolio</p>
          <h1>Sprzedaż</h1>
          <p className="page-head__sub">
            Udane płatności w dniu albo miesiącu, w walucie raportowej. Zwrot zdejmuje płatność
            z dnia, w którym wpłynęła.
          </p>
        </div>
        <div className="rangepicker" role="group" aria-label="Szczegółowość">
          <button type="button" aria-pressed={granularity === "day"} onClick={() => setGranularity("day")}>
            dziennie
          </button>
          <button
            type="button"
            aria-pressed={granularity === "month"}
            onClick={() => setGranularity("month")}
          >
            miesięcznie
          </button>
        </div>
      </header>

      <div className="sales-controls">
        <div className="rangepicker" role="group" aria-label="Zakres miesięcy">
          {presetList.map((item) => (
            <button
              key={item.key}
              type="button"
              aria-pressed={activePreset === item.key}
              onClick={() => {
                setFrom(item.from);
                setTo(item.to);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <label className="month-field">
          <span>od</span>
          <input type="month" value={from} max={current} onChange={(e) => e.target.value && setFrom(e.target.value)} />
        </label>
        <label className="month-field">
          <span>do</span>
          <input type="month" value={to} max={current} onChange={(e) => e.target.value && setTo(e.target.value)} />
        </label>
      </div>

      {invalid && <p className="form-error">Miesiąc „od” jest późniejszy niż „do”. Zamień je miejscami.</p>}

      {all.data && (
        <div className="series-toggles" role="group" aria-label="Produkty na wykresie">
          {all.data.series.map((item) => {
            const on = picked ? picked.has(item.product.slug) : true;
            const unmeasured = item.values === null;
            return (
              <button
                key={item.product.slug}
                type="button"
                className="series-toggle"
                aria-pressed={on}
                disabled={unmeasured}
                title={unmeasured ? (item.reason ?? undefined) : undefined}
                onClick={() => toggle(item.product.slug)}
              >
                <i style={{ background: colorOf(item.product.slug) }} aria-hidden="true" />
                <span>{item.product.name}</span>
                <b className="mono">
                  {unmeasured ? "brak płatności" : money(item.total_minor ?? 0, all.data.reporting_currency)}
                </b>
              </button>
            );
          })}
          {all.data.series.filter((item) => item.values !== null).length > 1 && (
            <button
              type="button"
              className="series-toggle series-toggle--total"
              aria-pressed={showTotal}
              onClick={() => setShowTotal((value) => !value)}
            >
              <i aria-hidden="true" />
              <span>Linia sumy</span>
            </button>
          )}
        </div>
      )}

      {picked?.size === 0 ? (
        <Empty title="Nie wybrano żadnego produktu" hint="Włącz co najmniej jeden produkt powyżej." />
      ) : sales.isPending && !invalid ? (
        <Loading />
      ) : sales.isError ? (
        <ErrorState error={sales.error} />
      ) : report ? (
        <>
          {report.total.total_minor !== null ? (
            <Headline
              label={`Sprzedaż · ${report.reporting_currency}`}
              value={moneyExact(report.total.total_minor, currency)}
              meta={
                <span>
                  {date(report.start)} — {date(report.through)}
                  {report.through < lastDayOf(to) && " · miesiąc w toku"}
                </span>
              }
              support={measured.slice(0, 3).map((item) => ({
                label: item.product.name,
                value: money(item.total_minor ?? 0, currency),
                foot: `${item.payments} płatności`,
              }))}
            />
          ) : null}

          <DataGaps gaps={gaps} />

          <Card
            title={granularity === "day" ? "Sprzedaż dzienna" : "Sprzedaż miesięczna"}
            note="Jedna linia na produkt. Przerywana linia to suma wybranych produktów, policzona przez backend."
          >
            <Chart
              option={option}
              height="tall"
              ariaLabel="Wykres liniowy sprzedaży w czasie, jedna linia na produkt"
              emptyLabel={report.total.reason ?? "Brak płatności w tym zakresie."}
            />
          </Card>

          {option && <SalesTable report={report} categories={categories} colorOf={colorOf} />}
        </>
      ) : null}
    </>
  );
}

function lastDayOf(month: string) {
  const [year, index] = month.split("-").map(Number) as [number, number];
  const last = new Date(year, index, 0).getDate();
  return `${month}-${String(last).padStart(2, "0")}`;
}

function SalesTable({
  report,
  categories,
  colorOf,
}: {
  report: SalesReport;
  categories: string[];
  colorOf: (slug: string) => string;
}) {
  const measured = report.series.filter((item) => item.values !== null);
  const withTotal = measured.length > 1 && report.total.values;
  return (
    <Card title="Tabela" flush>
      <details className="sales-table">
        <summary>
          Pokaż wartości {report.granularity === "day" ? "dzienne" : "miesięczne"} ({categories.length})
        </summary>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>{report.granularity === "day" ? "Dzień" : "Miesiąc"}</th>
                {measured.map((item) => (
                  <th key={item.product.slug} className="num">
                    <i className="swatch" style={{ background: colorOf(item.product.slug) }} aria-hidden="true" />
                    {item.product.name}
                  </th>
                ))}
                {withTotal && <th className="num">Wszystkie</th>}
              </tr>
            </thead>
            <tbody>
              {categories.map((label, index) => (
                <tr key={report.buckets[index]}>
                  <td className="mono">{label}</td>
                  {measured.map((item) => (
                    <td key={item.product.slug} className="mono num">
                      {moneyExact(item.values![index] ?? 0, report.reporting_currency)}
                    </td>
                  ))}
                  {withTotal && (
                    <td className="mono num">
                      {moneyExact(report.total.values![index] ?? 0, report.reporting_currency)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </Card>
  );
}
